import { InjectionToken } from '@angular/core';
import { Observable } from 'rxjs';
import { TemplatePortal } from '@angular/cdk/portal';

// =========================================================================
// Configuration tokens
// =========================================================================

/** Whether dark mode feature is enabled for the layout. */
export const CORE_LAYOUT_DARK_MODE_ENABLED = new InjectionToken<boolean>('CORE_LAYOUT_DARK_MODE_ENABLED');

/** Initial open/closed state of the sidenav. */
export const CORE_LAYOUT_SIDENAV_OPENED = new InjectionToken<boolean>('CORE_LAYOUT_SIDENAV_OPENED');

/** Minimal environment configuration required by the layout. */
export interface CoreLayoutEnvironment {
  UserGuid: string;
  CompanyGuid: string;
  BusinessPlanGuid: string;
  ProductGuid: string;
  SupportUserGuid: string;
  isLocalWeb: boolean;
  B2C_signout: string;
  urlsUnAvailableOffline: string;
  [key: string]: any;
}
export const CORE_LAYOUT_ENVIRONMENT = new InjectionToken<CoreLayoutEnvironment>('CORE_LAYOUT_ENVIRONMENT');

/** Role GUIDs mapping for component-level permissions (e.g. menu_users). */
export const CORE_LAYOUT_COMPONENTS_BY_ROLE = new InjectionToken<{ menu_users: string[]; [key: string]: string[] }>(
  'CORE_LAYOUT_COMPONENTS_BY_ROLE',
);

// =========================================================================
// Enum
// =========================================================================

export enum eEdgeMigrationStatus {
  UpToDate = 1,
  Downloading,
  ReadyToUpdate,
}

// =========================================================================
// Service interfaces (gateways)
// =========================================================================

export interface ICoreLayoutTranslationService {
  pt(key: string, args?: any[]): string;
  lang(pipeId: string): void;
  detectChange: Observable<any>;
}

export interface ICoreLayoutUpdateCommonService {
  companyData$: Observable<{ Image?: string; CompanyName?: string; [key: string]: any }>;
  profileData$: Observable<{
    Name?: string;
    LastName?: string;
    UserName?: string;
    Image?: string;
    [key: string]: any;
  }>;
  UpdateProfileData(): void;
}

export interface ICoreLayoutMenuRepository {
  MenusByUser(request: any): Observable<any>;
}

export interface ICoreLayoutRoleRepository {
  getRolesbyUser(): Observable<any[]>;
}

export interface ICoreLayoutAppRepository {
  ApplicationsByUser(request: any): Observable<any[]>;
  ApplicationModules(request: any): Observable<any[]>;
}

export interface ICoreLayoutDashboardMenuService {
  removePadding$: Observable<boolean>;
  menuToogleBehavior(isOpen: boolean): void;
}

export interface ICoreLayoutModalPortalService {
  components: (TemplatePortal<any> | any)[];
}

export interface ICoreLayoutArkikStatusService {
  isArkikOnline(): boolean;
  getOfflineToken(): string;
  setSessionClosed(): void;
  migrationStatus$: Observable<eEdgeMigrationStatus>;
}

export interface ICoreLayoutBomNotificationsService {
  badgeLabel$: Observable<string>;
  bellNotifications$: Observable<any[]>;
  bellDotVisible$: Observable<boolean>;
  toast$: Observable<any>;
}

export interface ICoreLayoutTableConfigService {
  tableActive: { asObservable(): Observable<boolean> };
}

export interface ICoreLayoutParametersDataApiService {
  getParametersData(params: any[]): Observable<string>;
}

export interface ICoreLayoutAlertService {
  setContainer(vcr: any): void;
  show(options: { message: string; variant: string; duration?: number; keepOpen?: boolean; id?: string }): void;
  success(msg: string, duration?: number, keepOpen?: boolean, id?: string): void;
  warning(msg: string, duration?: number, keepOpen?: boolean, id?: string): void;
  error(msg: string, duration?: number, keepOpen?: boolean, id?: string): void;
  removeAlertById(id: string): void;
}

export interface ICoreLayoutManageIdbService {
  select(catalog: string): Promise<any>;
  getRecord(catalog: string, key: string): Promise<any>;
}

export interface ICoreLayoutScrollBusService {
  emit(event: Event): void;
}

export interface ICoreLayoutActionBarPortalService {
  portal$: Observable<any>;
  forceLayoutClass$: Observable<boolean>;
}

// ---- Global header specific ----

export interface ICoreLayoutTrackingApiService {
  getAlertsByuser(userGuid: string): Observable<any[]>;
  markAlertAsRead(userGuid: string, alertId: number): Observable<any>;
  markAllAsRead(userGuid: string): Observable<any>;
}

export interface ICoreLayoutDispatchService {
  subgroupsSelected: Observable<any[]>;
}

export interface ICoreLayoutParameterService {
  setIsCemexParameter(): void;
  isCemexAccount$: Observable<boolean>;
  getMultipleParametersByDetails(params: any[]): Observable<any[]>;
}

export interface ICoreLayoutSvgIconCacheService {
  resolveAssetSrc(icon: string): Promise<string | any>;
}

// =========================================================================
// Service tokens
// =========================================================================

export const CORE_LAYOUT_TRANSLATION_SERVICE = new InjectionToken<ICoreLayoutTranslationService>(
  'CORE_LAYOUT_TRANSLATION_SERVICE',
);
export const CORE_LAYOUT_UPDATE_COMMON_SERVICE = new InjectionToken<ICoreLayoutUpdateCommonService>(
  'CORE_LAYOUT_UPDATE_COMMON_SERVICE',
);
export const CORE_LAYOUT_MENU_REPOSITORY = new InjectionToken<ICoreLayoutMenuRepository>('CORE_LAYOUT_MENU_REPOSITORY');
export const CORE_LAYOUT_ROLE_REPOSITORY = new InjectionToken<ICoreLayoutRoleRepository>('CORE_LAYOUT_ROLE_REPOSITORY');
export const CORE_LAYOUT_APP_REPOSITORY = new InjectionToken<ICoreLayoutAppRepository>('CORE_LAYOUT_APP_REPOSITORY');
export const CORE_LAYOUT_DASHBOARD_MENU = new InjectionToken<ICoreLayoutDashboardMenuService>(
  'CORE_LAYOUT_DASHBOARD_MENU',
);
export const CORE_LAYOUT_MODAL_PORTAL = new InjectionToken<ICoreLayoutModalPortalService>('CORE_LAYOUT_MODAL_PORTAL');
export const CORE_LAYOUT_ARKIK_STATUS = new InjectionToken<ICoreLayoutArkikStatusService>('CORE_LAYOUT_ARKIK_STATUS');
export const CORE_LAYOUT_BOM_NOTIFICATIONS = new InjectionToken<ICoreLayoutBomNotificationsService>(
  'CORE_LAYOUT_BOM_NOTIFICATIONS',
);
export const CORE_LAYOUT_TABLE_CONFIG = new InjectionToken<ICoreLayoutTableConfigService>('CORE_LAYOUT_TABLE_CONFIG');
export const CORE_LAYOUT_PARAMETERS_DATA_API = new InjectionToken<ICoreLayoutParametersDataApiService>(
  'CORE_LAYOUT_PARAMETERS_DATA_API',
);
export const CORE_LAYOUT_ALERT_SERVICE = new InjectionToken<ICoreLayoutAlertService>('CORE_LAYOUT_ALERT_SERVICE');
export const CORE_LAYOUT_MANAGE_IDB = new InjectionToken<ICoreLayoutManageIdbService>('CORE_LAYOUT_MANAGE_IDB');
export const CORE_LAYOUT_SCROLL_BUS = new InjectionToken<ICoreLayoutScrollBusService>('CORE_LAYOUT_SCROLL_BUS');
export const CORE_LAYOUT_ACTION_BAR_PORTAL = new InjectionToken<ICoreLayoutActionBarPortalService>(
  'CORE_LAYOUT_ACTION_BAR_PORTAL',
);
export const CORE_LAYOUT_TRACKING_API = new InjectionToken<ICoreLayoutTrackingApiService>('CORE_LAYOUT_TRACKING_API');
export const CORE_LAYOUT_DISPATCH_SERVICE = new InjectionToken<ICoreLayoutDispatchService>(
  'CORE_LAYOUT_DISPATCH_SERVICE',
);
export const CORE_LAYOUT_PARAMETER_SERVICE = new InjectionToken<ICoreLayoutParameterService>(
  'CORE_LAYOUT_PARAMETER_SERVICE',
);
export const CORE_LAYOUT_SVG_ICON_CACHE = new InjectionToken<ICoreLayoutSvgIconCacheService>(
  'CORE_LAYOUT_SVG_ICON_CACHE',
);
